import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react'
import { Branch, CreateBranchRequest, UpdateBranchRequest } from '@/types/branches'
import {
  getBranches as fetchBranchesFromApi,
  getBranchById as fetchBranchByIdFromApi,
  createBranch as createBranchInApi,
  updateBranch as updateBranchInApi,
  deleteBranch as deleteBranchInApi
} from '@/utils/branches'
import { useAuth } from '@/contexts/AuthContext'

interface BranchContextType {
  branches: Branch[]
  loading: boolean
  error: string | null
  fetchBranches: () => Promise<void>
  fetchBranchById: (id: string) => Promise<Branch>
  createBranch: (branch: CreateBranchRequest) => Promise<void>
  updateBranch: (branch: UpdateBranchRequest) => Promise<void>
  deleteBranch: (id: string) => Promise<void>
  getBranchById: (id: string) => Branch | undefined
}

const BranchContext = createContext<BranchContextType | undefined>(undefined)

interface BranchProviderProps {
  children: ReactNode
}

export function BranchProvider({ children }: BranchProviderProps) {
  const { isAuthenticated } = useAuth()
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBranches = useCallback(async () => {
    if (!isAuthenticated) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const liveBranches = await fetchBranchesFromApi()
      setBranches(liveBranches)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch branches')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchBranchById = useCallback(async (id: string): Promise<Branch> => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    const cachedBranch = branches.find((branch) => branch.id === id)
    if (cachedBranch) {
      return cachedBranch
    }

    setLoading(true)
    setError(null)
    try {
      const branch = await fetchBranchByIdFromApi(id)
      setBranches((prev) => {
        const others = prev.filter((item) => item.id !== branch.id)
        return [...others, branch].sort((a, b) => a.branchName.localeCompare(b.branchName))
      })
      return branch
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch branch'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [branches, isAuthenticated])

  const createBranch = useCallback(async (branchData: CreateBranchRequest) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const newBranch = await createBranchInApi(branchData)
      setBranches(prev => [...prev, newBranch].sort((a, b) => a.branchName.localeCompare(b.branchName)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create branch')
      throw err
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const updateBranch = useCallback(async (branchData: UpdateBranchRequest) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const updatedBranch = await updateBranchInApi(branchData)
      setBranches(prev =>
        prev
          .map(branch => (branch.id === branchData.id ? updatedBranch : branch))
          .sort((a, b) => a.branchName.localeCompare(b.branchName))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update branch')
      throw err
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const deleteBranch = useCallback(async (id: string) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      await deleteBranchInApi(id)
      setBranches(prev => prev.filter(branch => branch.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete branch')
      throw err
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const getBranchById = useCallback((id: string): Branch | undefined => {
    return branches.find(branch => branch.id === id)
  }, [branches])

  useEffect(() => {
    if (!isAuthenticated) {
      setBranches([])
      setError(null)
    }
  }, [isAuthenticated])

  const value: BranchContextType = {
    branches,
    loading,
    error,
    fetchBranches,
    fetchBranchById,
    createBranch,
    updateBranch,
    deleteBranch,
    getBranchById
  }

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  )
}

export function useBranches() {
  const context = useContext(BranchContext)
  if (context === undefined) {
    throw new Error('useBranches must be used within a BranchProvider')
  }
  return context
} 
